
import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from "reselect";
import { useIntl } from 'react-intl';

//Components
import { Timezone } from '../../../../components/uiControls'
import { TableView } from 'Components';
import Chip from 'Components/Chip';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';
import { useDataGridThrottler } from 'Core/hooks';
//Styles
import "./offlineDevice.scss"

import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);

const getData = createSelector([getUnitsCatalog, getReadingsCatalog], (units, readings) => {
    let data = [];
    units.map((unit) => {
        if (unit.show != undefined && unit.show === 0) {
            return;
        }
        let lastReading = readings[unit.id]
        let dataToPush = {
            ...unit,
            unitTime: lastReading?.unitTime,
            lastMessageTime: lastReading?.LastMessageTime || lastReading?.unitTime
        };
        data.push(dataToPush);
    });
    return data;
})

// Stable reference so useDataGridThrottler doesn't rebuild its throttle on every render
const noopOnChange = () => { };

const getFleetType = (settings) => settings ? JSON.parse(settings)?.fleet?.id : undefined;

const OfflineDevice = ({ item, update }) => {

    const targetRef = useRef();
    const { messages, locale } = useIntl();

    //Local States
    const [dimensions, setDimensions] = useState({ width: 0, height: 400 });
    const [fleetType, setFleetType] = useState(() => getFleetType(item.settings));
    const [hasFirstEmission, setHasFirstEmission] = useState(false);

    //Redux States
    const { updateResponse } = useSelector(state => state.gadgeOfflineDeviceRedux);
    const { user: { dateformat } } = useSelector((state) => state.securityRedux);
    const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits);

    const computedData = useSelector(state => getData(state) || []);
    const propertiesToValidate = useMemo(() => ["label", "isOffline"], []);

    // Realtime units/readings arrive from the socket every ~2s; the throttler keeps the list updates cheap
    const data = useDataGridThrottler(
        computedData,
        1000,
        noopOnChange,
        "id",
        propertiesToValidate
    );

    // This function calculates width and height of the list
    const getListSize = () => {
        if (targetRef.current) {
            setDimensions({
                width: targetRef.current.offsetWidth,
                height: targetRef.current.offsetHeight - 70
            });
        }
    };

    useLayoutEffect(() => {
        getListSize();
        window.addEventListener("resize", getListSize);
    }, []);

    useEffect(() => {
        setFleetType(getFleetType(item.settings));
    }, [item.settings, update]);

    useEffect(() => {
        if (updateResponse && updateResponse.id == item.id) {
            setFleetType(getFleetType(updateResponse.settings));
        }
    }, [updateResponse]);

    // The throttler's initial [] is not real data; flag when it emits for the first time
    const isMountEmission = useRef(true);
    useEffect(() => {
        if (isMountEmission.current) {
            isMountEmission.current = false;
            return;
        }
        setHasFirstEmission(true);
    }, [data]);

    // Loading tracks the units catalog load (plus the throttle catching up with it), never the socket updates,
    // so the table doesn't show "No matches found" while units are still arriving
    const loading = loadingUnits || !hasFirstEmission || (computedData.length > 0 && data.length === 0);

    const category = item.settings
        ? (fleetType == "Asset" ? messages['realtimemaps_map_settings_offline_asset'] : messages['realtimemaps_map_settings_offline_fleet'])
        : '';

    const dataDevice = useMemo(
        () => data.filter((unit) => unit?.deviceCategory === fleetType && unit.isOffline),
        [data, fleetType]
    );

    return (
        <div ref={targetRef}
            style={{
                overflow: 'hidden',
                height: '100%',
                width: '100%'
            }}>

            <div style={{ width: 150, marginBottom: 10 }}>
                <Chip label={category} />
            </div>

            <div className='offline-device-row'>
                <div style={{ fontWeight: "bold" }} className='offline-device-cell'>
                    <span>{messages['dashboardPanel_date']}</span>
                </div>
                <div style={{ fontWeight: "bold" }} className='offline-device-cell'>
                    <span>{messages['dashboardPanel_nameRegister']}</span>
                </div>
            </div>
            <TableView
                name="devices_offline"
                itemSelector={false}
                serverSide
                viewType="list"
                data={dataDevice || []}
                rowsPerPage={dataDevice?.length}
                height={`${dimensions.height - 20}px`}
                messages={messages}
                loading={loading}
                infiniteScroll={false}
                onChangePage={(newPage) => { }}
                moreRowsAvailable={true}
                renderRow={(row) => {
                    return (
                        <div className='offline-device-row'>
                            <div className='offline-device-cell'>
                                {row.lastMessageTime &&
                                    <Timezone
                                        date={new Date(row.lastMessageTime)}
                                        dateFormat={dateformat}
                                        timeFormat="hh:mm a"
                                        timezone={row?.timeZone?.name || "UTC"}
                                        className="vicinity-tool-date"
                                        customFormat={true}
                                        locale={locale}
                                        noWrap={false}
                                        oneControl={true}
                                    />
                                }
                            </div>
                            <div className='offline-device-cell'>
                                <span>{row.label}</span>
                            </div>
                        </div>
                    )
                }}
                noMoreItemsMessage={messages['noMoreItems']}
            >
            </TableView>
        </div>

    );
}

export default OfflineDevice;